import React, { useEffect, useRef, useState } from 'react'
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom'
import FloatingAIChat from './FloatingAIChat'
import { Sun, Moon, Play, Download, Menu, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { useAuthStore } from '../stores/auth'
import { useThemeStore } from '../stores/theme'
import { T } from '../pages/shared'
import ProcessFlow from './ProcessFlow'
import { NAV, getHeaderCopy, isNavActive } from '../nav/navConfig'
import primaryLogoCanyon from '../assets/4401-primary-canyon.png'
import logoIconMark from '../assets/4401-icon-mark.png'
import CommandPalette from './CommandPalette'
import KeyboardShortcuts from './KeyboardShortcuts'
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts'
import Breadcrumbs from './Breadcrumbs'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notificationsApi } from '../api/client'

function NavIcon({ type, size = 19 }: { type: string; size?: number }) {
  const s = { width: size, height: size, viewBox: '0 0 24 24' as const, fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  const paths: Record<string, React.ReactNode> = {
    home:        <><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    database:    <><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></>,
    settings:    <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></>,
    play:        <polygon points="5 3 19 12 5 21 5 3"/>,
    flag:        <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></>,
    edit:        <><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
    list:        <><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></>,
    layers:      <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>,
    'bar-chart': <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
    shield:      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>,
    users:       <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></>,
    'file-text': <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></>,
    download:    <><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
    book:        <><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></>,
    'log-out':   <><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>,
    folder:      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>,
    info:        <><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></>,
    user:        <><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
    'alert-triangle': <><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    'book-open': <><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></>,
    'check-circle':    <><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>,
    'message-circle':  <><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></>,
    'bell':            <><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></>,
    'activity':        <><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></>,
    'cpu':             <><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></>,
  }
  return <svg {...s}>{paths[type] ?? paths.list}</svg>
}

const MOBILE_BP = '(max-width: 960px)'
const MOBILE_DRAWER_W = 280
const SIDEBAR_EXPANDED_W = 252
const SIDEBAR_COLLAPSED_W = 72

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem('ds_sidebar_collapsed') === '1'
  } catch {
    return false
  }
}

function NotificationBell() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const qc = useQueryClient()

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(30, false).then(r => r.data),
    refetchInterval: 30_000,
  })
  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })
  const markAll = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })
  const clearAll = useMutation({
    mutationFn: () => notificationsApi.clearAll(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const notifications: any[] = data?.notifications || []
  const unread: number = data?.unread_count || 0

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const evtColor: Record<string, string> = {
    dqa_completed: T.olivine,
    dqa_failed: '#e85d04',
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'relative', width: 36, height: 36, borderRadius: T.rSm,
          border: `1px solid ${T.border2}`, background: 'var(--glass-chip-bg)',
          color: T.textSecondary, cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: 4, right: 4, width: 8, height: 8,
            borderRadius: '50%', background: T.canyon, border: `1.5px solid var(--surface-0)`,
          }} />
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 44, right: 0, zIndex: 3000,
          width: 340, maxHeight: 440, overflowY: 'auto',
          background: T.surface1, border: `1px solid ${T.border2}`,
          borderRadius: T.rSm, boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}>
          <div style={{ padding: '12px 16px 10px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>Notifications</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {unread > 0 && (
                <button type="button" onClick={() => markAll.mutate()} style={{ fontSize: 11, color: T.olivine, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button type="button" onClick={() => clearAll.mutate()} style={{ fontSize: 11, color: T.textMuted, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                  onMouseEnter={e => { e.currentTarget.style.color = T.canyon }}
                  onMouseLeave={e => { e.currentTarget.style.color = T.textMuted }}
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
          {notifications.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: T.textMuted }}>No notifications yet</div>
          ) : notifications.map((n: any) => (
            <div
              key={n.id}
              style={{
                padding: '10px 16px', borderBottom: `1px solid ${T.border}`,
                background: n.is_read ? 'transparent' : 'var(--surface-2)',
                cursor: 'pointer',
              }}
              onClick={() => { if (!n.is_read) markRead.mutate(n.id) }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', marginTop: 5, flexShrink: 0, background: evtColor[n.event_type] || T.textMuted, opacity: n.is_read ? 0.3 : 1 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.textPrimary, marginBottom: 2 }}>{n.title}</div>
                  <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.45 }}>{n.message}</div>
                  <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4 }}>
                    {n.created_at ? new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function Layout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const loc = useLocation()
  const routeParams = useParams<{ id?: string }>()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [desktopCollapsed, setDesktopCollapsed] = useState(readSidebarCollapsed)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  // B1-#9: register global keyboard shortcuts
  useKeyboardShortcuts(() => setShortcutsOpen(v => !v))
  const { theme, toggle: toggleTheme } = useThemeStore()
  const isLight = theme === 'light'
  const isMobile = useMediaQuery(MOBILE_BP)

  useEffect(() => {
    setMobileOpen(false)
  }, [loc.pathname])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(o => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!isMobile) setMobileOpen(false)
  }, [isMobile])

  const toggleSidebarCollapsed = () => {
    setDesktopCollapsed(c => {
      const next = !c
      try {
        localStorage.setItem('ds_sidebar_collapsed', next ? '1' : '0')
      } catch {
        /* ignore */
      }
      return next
    })
  }

  const [title, sub] = getHeaderCopy(loc.pathname, routeParams)
  const initials = (user?.full_name || 'SA').split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
  const roleLabel = user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Analyst'

  const collapsed = !isMobile && desktopCollapsed
  const sideW = isMobile ? MOBILE_DRAWER_W : collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_EXPANDED_W

  const chevronBtn: React.CSSProperties = {
    flexShrink: 0,
    width: 34,
    height: 34,
    borderRadius: T.rSm,
    border: `1px solid ${T.border2}`,
    background: T.surface2,
    color: T.textSecondary,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
  }

  const sidebarInner = (
    <>
      <div
        style={{
          padding: collapsed ? '10px 6px 12px' : '18px 14px 14px',
          display: 'flex',
          flexDirection: collapsed ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: collapsed ? 'flex-start' : 'space-between',
          gap: collapsed ? 10 : 8,
          flexShrink: 0,
        }}
      >
        {!isMobile && collapsed && (
          <button
            type="button"
            aria-expanded={false}
            aria-label="Expand sidebar"
            title="Expand sidebar"
            onClick={toggleSidebarCollapsed}
            style={{ ...chevronBtn, alignSelf: 'center' }}
            onMouseEnter={e => {
              e.currentTarget.style.background = T.surface3
              e.currentTarget.style.color = T.textPrimary
              e.currentTarget.style.borderColor = T.canyon
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = T.surface2
              e.currentTarget.style.color = T.textSecondary
              e.currentTarget.style.borderColor = T.border2
            }}
          >
            <ChevronRight size={18} strokeWidth={2} aria-hidden />
          </button>
        )}
        <div
          role={collapsed ? 'button' : undefined}
          tabIndex={collapsed ? 0 : undefined}
          onKeyDown={collapsed ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/dashboard') } } : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: collapsed ? 0 : 11,
            cursor: 'pointer',
            flexShrink: 0,
            minWidth: 0,
            justifyContent: 'center',
            flex: collapsed ? undefined : 1,
            width: collapsed ? '100%' : undefined,
          }}
          onClick={() => navigate('/dashboard')}
        >
          {collapsed ? (
            <img
              src={logoIconMark}
              alt="44.01"
              style={{
                width: 36,
                height: 36,
                objectFit: 'contain',
                display: 'block',
                flexShrink: 0,
              }}
            />
          ) : (
            <img
              src={primaryLogoCanyon}
              alt="44.01"
              style={{ height: 36, width: 'auto', maxWidth: 'min(200px, 100%)', display: 'block', objectFit: 'contain', flexShrink: 0 }}
            />
          )}
        </div>
        {!isMobile && !collapsed && (
          <button
            type="button"
            aria-expanded
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
            onClick={e => {
              e.stopPropagation()
              toggleSidebarCollapsed()
            }}
            style={chevronBtn}
            onMouseEnter={e => {
              e.currentTarget.style.background = T.surface3
              e.currentTarget.style.color = T.textPrimary
              e.currentTarget.style.borderColor = T.canyon
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = T.surface2
              e.currentTarget.style.color = T.textSecondary
              e.currentTarget.style.borderColor = T.border2
            }}
          >
            <ChevronLeft size={18} strokeWidth={2} aria-hidden />
          </button>
        )}
      </div>

      {!collapsed && (
      <div style={{ margin: '0 12px 18px', padding: '12px 14px', borderRadius: T.rSm,
        background: 'var(--glass-chip-bg)', border: `1px solid var(--glass-card-border)`,
        backdropFilter: 'blur(12px) saturate(1.15)', WebkitBackdropFilter: 'blur(12px) saturate(1.15)',
        flexShrink: 0, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)' }}>
        <div style={{ fontSize: 10, fontWeight: 700, fontFamily: T.fontDisplay, textTransform: 'uppercase', letterSpacing: 1.5, color: T.textMuted, lineHeight: 1.8 }}>
          Crafted · Engineered · Designed
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, fontFamily: T.fontDisplay, color: T.textPrimary, letterSpacing: -0.2 }}>
          Digitalisation Team 4401
        </div>
      </div>
      )}

      <nav aria-label="Primary" style={{ flex: 1, padding: collapsed ? '0 6px' : '0 8px', overflowY: 'auto', overflowX: 'hidden' }}>
        {NAV.filter(section => {
          const userRole = user?.role ?? ''
          const isAdmin = userRole === 'admin' || userRole === 'super_admin'
          // Admin and Super Admin always see everything
          if (isAdmin) return true
          // Always-visible sections (Compliance, Help)
          if (!section.platform) return true
          // Admin-only section — non-admins never see it
          if (section.platform === 'admin') return false
          // Check platform_access — null means all enabled (backward-compatible)
          const pa = (user as any)?.platform_access
          if (!pa) return true  // no restrictions set → show all
          return pa[section.platform] === true
        }).map(section => (
          <div key={section.section} style={{ marginBottom: 4 }}>
            {!collapsed && (
              <div style={{ fontSize: 10, fontFamily: T.fontDisplay, fontWeight: 700, letterSpacing: 1.6, color: T.textMuted, padding: '12px 10px 7px', whiteSpace: 'nowrap' }}>
                {section.section}
              </div>
            )}
            {section.items.map(item => {
              const active = isNavActive(loc.pathname, item.to)
              return (
                <button
                  type="button"
                  key={item.to}
                  title={collapsed ? item.label : undefined}
                  onClick={() => { navigate(item.to); if (isMobile) setMobileOpen(false) }}
                  style={{
                    display: 'flex', alignItems: 'center',
                    gap: collapsed ? 0 : 13,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    padding: collapsed ? '10px 8px' : '10px 12px',
                    width: '100%', textAlign: 'left',
                    border: 'none', borderRadius: T.rSm, cursor: 'pointer',
                    marginBottom: 2, transition: 'background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
                    background: active ? 'var(--nav-active-bg)' : 'transparent',
                    color: active ? T.textPrimary : T.textSecondary,
                    fontFamily: T.fontBody, fontSize: 14, fontWeight: active ? 600 : 450,
                    boxShadow: !collapsed && active ? `inset 3px 0 0 var(--nav-active-edge)` : 'none',
                  }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.background = T.surface3; e.currentTarget.style.color = T.textPrimary } }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.textSecondary } }}
                >
                  <span style={{ color: active ? T.olivine : 'inherit', display: 'flex', flexShrink: 0 }}>
                    <NavIcon type={item.icon} size={collapsed ? 20 : 19} />
                  </span>
                  {!collapsed && <span style={{ whiteSpace: 'nowrap' }}>{item.label}</span>}
                </button>
              )
            })}
          </div>
        ))}
      </nav>

      <div style={{ padding: collapsed ? '8px 6px 8px' : '8px 12px 10px', flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('startTour'))}
          title="Take a Tour"
          style={{
            width: '100%',
            padding: collapsed ? '10px 8px' : '10px 14px',
            borderRadius: T.rSm,
            border: `1px solid ${T.border}`,
            background: T.surface2,
            color: T.textSecondary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: collapsed ? 0 : 10,
            fontFamily: T.fontBody,
            fontSize: 13,
            transition: 'background 0.15s, color 0.15s',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = T.surface3; e.currentTarget.style.color = T.textPrimary }}
          onMouseLeave={e => { e.currentTarget.style.background = T.surface2; e.currentTarget.style.color = T.textSecondary }}
        >
          <NavIcon type="info" size={15} />
          {!collapsed && 'Take a Tour'}
        </button>
      </div>

      <div style={{ padding: collapsed ? '8px 6px 12px' : '8px 12px 12px', flexShrink: 0, borderTop: `1px solid ${T.border}`, marginTop: 'auto' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: collapsed ? 0 : 10,
            marginBottom: 10,
            padding: collapsed ? '8px 6px' : '10px 12px',
            borderRadius: T.rSm,
            background: T.surface2,
            border: `1px solid ${T.border2}`,
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <div
            style={{
              width: collapsed ? 36 : 40,
              height: collapsed ? 36 : 40,
              borderRadius: '50%',
              background: T.canyonDim,
              color: T.canyon,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: collapsed ? 12 : 13,
              fontWeight: 800,
              fontFamily: T.fontDisplay,
              flexShrink: 0,
            }}
            title={user?.full_name || 'System Admin'}
            aria-hidden
          >
            {initials}
          </div>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.full_name || 'System Admin'}</div>
              <div style={{ fontSize: 10, fontWeight: 700, fontFamily: T.fontDisplay, letterSpacing: 0.8, color: T.olivine, textTransform: 'uppercase', marginTop: 4 }}>{roleLabel}</div>
            </div>
          )}
        </div>
        <button
          type="button"
          title="Logout"
          onClick={() => { logout(); navigate('/login'); if (isMobile) setMobileOpen(false) }}
          style={{
            width: '100%', padding: collapsed ? '10px 8px' : '10px 12px', borderRadius: T.rSm, border: `1px solid ${T.border2}`,
            background: T.surface2, color: T.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: collapsed ? 0 : 8,
            fontFamily: T.fontBody, fontSize: 13, fontWeight: 600, transition: 'background 0.15s, color 0.15s, border-color 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = T.canyonDim; e.currentTarget.style.color = T.canyon; e.currentTarget.style.borderColor = T.canyon }}
          onMouseLeave={e => { e.currentTarget.style.background = T.surface2; e.currentTarget.style.color = T.textSecondary; e.currentTarget.style.borderColor = T.border2 }}
        >
          <NavIcon type="log-out" size={18} />
          {!collapsed && <span>Logout</span>}
        </button>
        {!collapsed && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}`, fontSize: 10, color: T.textMuted, textAlign: 'center', lineHeight: 1.5 }}>
            <div style={{ fontFamily: T.fontDisplay, fontWeight: 700, color: T.textSecondary }}>44.01 © 2026</div>
            <div style={{ marginTop: 4, fontFamily: T.fontMono, fontSize: 9, opacity: 0.85 }}>UI {__APP_VERSION__}</div>
            <div style={{ marginTop: 4 }}>Designed and built by 44.01 Digitalisation team</div>
          </div>
        )}
      </div>
    </>
  )

  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      {shortcutsOpen && <KeyboardShortcuts onClose={() => setShortcutsOpen(false)} />}
      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'transparent', fontFamily: T.fontBody }}>
        {isMobile && mobileOpen && (
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 1190, border: 'none', padding: 0,
              background: 'rgba(0,0,0,0.45)', cursor: 'pointer',
            }}
          />
        )}

        <aside
          aria-label="Main navigation"
          style={{
            position: isMobile ? 'fixed' : 'relative',
            left: isMobile ? (mobileOpen ? 0 : -MOBILE_DRAWER_W) : undefined,
            top: 0,
            bottom: 0,
            zIndex: isMobile ? 1200 : undefined,
            width: sideW,
            minWidth: sideW,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--glass-sidebar-bg)',
            backdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
            WebkitBackdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
            borderRight: `1px solid var(--glass-sidebar-border)`,
            boxShadow: isLight
              ? '4px 0 20px rgba(28,25,23,0.05)'
              : '4px 0 32px rgba(0,0,0,0.5), inset -1px 0 0 rgba(255,255,255,0.08)',
            flexShrink: 0,
            transition: isMobile ? 'left 0.22s cubic-bezier(0.16,1,0.3,1)' : 'width 0.22s ease, min-width 0.22s ease',
            overflow: 'hidden',
          }}
        >
          {sidebarInner}
        </aside>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'transparent', minWidth: 0 }}>
          <header role="banner" style={{
            padding: '0 16px 0 20px',
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--header-bg)',
            backdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
            WebkitBackdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
            borderBottom: `1px solid ${T.border2}`,
            flexShrink: 0,
            gap: 12,
            position: 'relative',
            zIndex: 100,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
              {isMobile && (
                <button
                  type="button"
                  aria-label="Open navigation menu"
                  onClick={() => setMobileOpen(true)}
                  style={{ background: T.surface2, border: `1px solid ${T.border2}`, color: T.textSecondary, cursor: 'pointer', borderRadius: T.rSm, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                >
                  <Menu size={20} strokeWidth={2} aria-hidden />
                </button>
              )}
              <div style={{ minWidth: 0 }}>
                <h1 style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 800, color: T.textPrimary, letterSpacing: -0.4, lineHeight: 1, margin: 0 }}>{title}</h1>
                <p style={{ fontSize: 11, color: T.textMuted, marginTop: 3, marginBottom: 0 }}>{sub}</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                aria-label="Search pages, keyboard shortcut Command K"
                title="Go to page (⌘K)"
                style={{ width: 36, height: 36, borderRadius: T.rSm, border: `1px solid ${T.border2}`,
                  background: 'var(--glass-chip-bg)', color: T.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
              >
                <Search size={17} strokeWidth={2} aria-hidden />
              </button>
              <NotificationBell />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', borderRadius: 999,
                background: 'var(--glass-chip-bg)', border: `1px solid color-mix(in srgb, var(--olivine) 30%, transparent)`,
                backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)' }}>
                <div className="pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: T.olivine }} />
                <span style={{ fontSize: 10, fontFamily: T.fontDisplay, fontWeight: 700, color: T.olivine, letterSpacing: 0.6 }}>LIVE</span>
              </div>
              {/* B1-#9: keyboard shortcuts hint */}
              <button
                type="button"
                onClick={() => setShortcutsOpen(v => !v)}
                aria-label="Keyboard shortcuts"
                title="Keyboard shortcuts (?)"
                style={{ width: 36, height: 36, borderRadius: T.rSm, border: `1px solid ${T.border2}`,
                  background: 'var(--glass-chip-bg)', color: T.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  fontFamily: T.fontMono, fontSize: 14, fontWeight: 700,
                  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', transition: 'border-color 0.2s, color 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.canyon; e.currentTarget.style.color = T.canyon }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border2; e.currentTarget.style.color = T.textMuted }}
              >
                ?
              </button>
              <button
                type="button"
                onClick={toggleTheme}
                aria-label={isLight ? 'Switch to dark theme' : 'Switch to light theme'}
                title={isLight ? 'Dark mode' : 'Light mode'}
                style={{ width: 36, height: 36, borderRadius: T.rSm, border: `1px solid ${T.border2}`,
                  background: 'var(--glass-chip-bg)', color: T.textPrimary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'border-color 0.2s, color 0.2s, background 0.2s',
                  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.canyon; e.currentTarget.style.color = T.canyon; e.currentTarget.style.background = T.canyonDim }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border2; e.currentTarget.style.color = T.textPrimary; e.currentTarget.style.background = 'var(--glass-chip-bg)' }}
              >
                {isLight ? <Moon size={18} strokeWidth={2} aria-hidden /> : <Sun size={18} strokeWidth={2} aria-hidden />}
              </button>
              <button type="button" onClick={() => navigate('/run')} style={{ padding: '7px 14px', borderRadius: T.rSm, border: `1px solid ${T.border2}`,
                background: 'var(--glass-chip-bg)', color: T.textPrimary, cursor: 'pointer', fontSize: 12, fontFamily: T.fontBody, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'background 0.15s, border-color 0.15s',
                backdropFilter: 'blur(12px) saturate(1.15)', WebkitBackdropFilter: 'blur(12px) saturate(1.15)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)' }}
                onMouseEnter={e => { e.currentTarget.style.background = T.surface3 }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--glass-chip-bg)' }}>
                <Play size={14} strokeWidth={2.25} fill="currentColor" fillOpacity={0.15} aria-hidden />
                Run DQA
              </button>
              <button type="button" onClick={() => navigate('/reports')} style={{ padding: '7px 14px', borderRadius: T.rSm, border: 'none', background: T.canyon, color: T.offWhite, cursor: 'pointer', fontSize: 12, fontFamily: T.fontBody, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, boxShadow: `var(--shadow-canyon), inset 0 1px 0 rgba(255,255,255,0.28)`, transition: 'filter 0.15s, transform 0.12s' }}
                onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.06)' }}
                onMouseLeave={e => { e.currentTarget.style.filter = 'none' }}>
                <Download size={14} strokeWidth={2.5} aria-hidden />
                Export
              </button>
            </div>
          </header>
          <main
            id="main-content"
            role="main"
            aria-label="Main content"
            tabIndex={-1}
            style={{ flex: 1, overflowY: 'auto', padding: '16px 18px 22px', position: 'relative', isolation: 'isolate' as const }}
          >
            <Breadcrumbs />
            <ProcessFlow />
            <Outlet />
          </main>
        </div>
      </div>
      <FloatingAIChat />
    </>
  )
}
